import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Component, StrictMode } from "react";
import type React from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { apiErrorMessage } from "./api";
import "./styles.css";
import { ToastProvider, notifyError } from "./toast";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // A 401 means sign in again — retrying just delays that.
      retry: (failureCount, error) =>
        (error as { status?: number }).status === 401 ? false : failureCount < 2,
    },
  },
  // Centralized here rather than per call site: a page-specific `onError`
  // still runs for its own field-level detail, but every query and mutation
  // in the app — 50-odd call sites across apps/web/src/pages — gets a toast
  // for free, so a failure can never go unnoticed just because nobody wired
  // one up locally. `meta: { silent: true }` opts a query out for the one
  // case that isn't really a failure: the "am I signed in" check on first
  // load, whose 401 is the normal signed-out state.
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.meta?.silent) return;
      notifyError(apiErrorMessage(error, "Couldn't load. Check your connection and try again."));
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      notifyError(apiErrorMessage(error));
    },
  }),
});

// A last resort for whatever the two caches above don't cover — a render
// throw, or a rejected promise nobody awaited — so a failure surfaces as a
// toast instead of a silent blank screen with nothing but a console line.
window.addEventListener("error", (e) => {
  notifyError(e.error instanceof Error ? e.error.message : "Something went wrong.");
});
window.addEventListener("unhandledrejection", (e) => {
  notifyError(e.reason instanceof Error ? e.reason.message : "Something went wrong.");
});

class RootErrorBoundary extends Component<{ children: React.ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch(error: unknown) {
    notifyError(error instanceof Error ? error.message : "Something went wrong.");
  }

  override render() {
    if (this.state.failed) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-neutral-50">
          <div className="max-w-sm text-center">
            <h1 className="font-[family-name:--font-display] text-xl font-semibold tracking-tight text-neutral-900">
              Something went wrong
            </h1>
            <p className="mt-2 text-sm text-neutral-500">
              Reloading the page usually fixes it. If it keeps happening, let us know.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-4 rounded-lg bg-accent-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-accent-500"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const root = document.getElementById("root");
if (!root) throw new Error("#root is missing from index.html");

createRoot(root).render(
  <StrictMode>
    <ToastProvider>
      <RootErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </RootErrorBoundary>
    </ToastProvider>
  </StrictMode>,
);
